import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';

import { TsButtonModule } from 'tsui';
import { Pipeline } from 'app/modules/projects/projects.types';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ReverseGeoCode, Parcel } from 'app/modules/research/research.types';

const MATERIAL_MODULES = [MatFormFieldModule, MatInputModule, MatSelectModule, MatTooltipModule, MatProgressBarModule];

@Component({
  selector: 'save-parcel-modal',
  templateUrl: './save-parcel-modal.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ...MATERIAL_MODULES, TsButtonModule],
  styles: [
    `
      :host {
        display: block;
        min-width: 50vw;
        max-width: 50vw;
        width: 100%;
      }
    `,
  ],
})
export class SaveParcelModalComponent implements OnInit {
  addProjectForm: UntypedFormGroup;
  pipelines: Pipeline[];
  parcel: Parcel | any;
  reverseGeoCode: ReverseGeoCode;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { parcel: Parcel; reverseGeoCode: ReverseGeoCode; pipelines: Pipeline[] },
    private readonly matDialogRef: MatDialogRef<SaveParcelModalComponent>,
    private readonly projectsService: ProjectsService,
    private readonly formBuilder: UntypedFormBuilder,
  ) {
    this.addProjectForm = this.formBuilder.group({
      title: ['', [Validators.required]],
      pipeline_id: ['', [Validators.required]],
      remote_id: [''],
      property: this.formBuilder.group({
        remote_id: [''],
        address: this.formBuilder.group({
          address_string: '',
          latitude: '',
          longitude: '',
          city: '',
          state: '',
        }),
      }),
    });
  }

  ngOnInit(): void {
    this.pipelines = this.data.pipelines.filter((pipeline) => pipeline?.pipeline_type !== 'dynamic');

    if (this.data.parcel) {
      this.patchFormWhenParcelExists();
    } else if (this.data.reverseGeoCode) {
      this.patchFormWhenReverseGeoCodeExists();
    }
  }

  private patchFormWhenParcelExists(): void {
    this.parcel = this.data.parcel;

    this.addProjectForm.patchValue({
      title: this.parcel.taxAssessor.propertyAddressFull,
      property: {
        remote_id: this.parcel.parcel.id,
        address: {
          address_string: `
            ${this.parcel.parcel.addrLine1} ${this.parcel.parcel.city} ${this.parcel.parcel.state}, ${this.parcel.parcel.zip}
            `,
          latitude: this.parcel.parcel.latitude,
          longitude: this.parcel.parcel.longitude,
        },
      },
      remote_id: this.parcel.parcel.id,
    });
  }

  private patchFormWhenReverseGeoCodeExists(): void {
    this.reverseGeoCode = this.data.reverseGeoCode;

    this.addProjectForm.patchValue({
      title: this.reverseGeoCode.address,
      property: {
        address: {
          address_string: this.reverseGeoCode.address,
          latitude: this.reverseGeoCode.lat,
          longitude: this.reverseGeoCode.lng,
          city: this.reverseGeoCode.city,
          state: this.reverseGeoCode.state,
        },
      },
    });
  }

  discard(): void {
    this.matDialogRef.close();
  }

  save(): void {
    this.addProjectForm.disable();

    const project = this.addProjectForm.value;

    this.projectsService
      .saveProject(project)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((project) => {
        this.matDialogRef.close(project);
      });
  }
}
